import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { SelectableDealer } from 'remarketing-angular-library/states/search-filter/interfaces/selectable-dealer.interface';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { DealerFilter, SearchFilterQueries } from 'remarketing-angular-library';
import {
  distinctUntilChanged,
  map, mapTo,
  scan,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { FilterState } from '../../interfaces/filter-state.interface';
import { TranslocoDirective } from '@jsverse/transloco';
import { TrackButtonDirective } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';

export function dealersShown(initial: number, perStep: number) {
  return (step: number) => initial + step * perStep;
}

export function maxStepsForCount(initial: number, perStep: number) {
  return (count: number) => Math.ceil((count - initial) / perStep);
}
export function stepForCountReducer(initialStep: number, perStep: number): (acc: number, val: number) => number;
export function stepForCountReducer(initialStep, perStep) {
  const maxSteps = maxStepsForCount(initialStep, perStep);
  let totalItems;
  return (step, currentTotalItems) => {
    const steps = maxSteps(currentTotalItems);
    if (totalItems && currentTotalItems !== totalItems) {
      totalItems = currentTotalItems;
      return Math.min(steps, step);
    }
    totalItems = currentTotalItems;
    const nextStep = step + 1;
    if (nextStep > steps) {
      return 0;
    }

    return nextStep;
  };
}

@Component({
    selector: 'lib-dealer-filter',
    templateUrl: './dealer-filter.component.html',
    styleUrls: ['./dealer-filter.component.scss'],
    imports: [TranslocoDirective, TrackButtonDirective, CommonModule]
})
export class DealerFilterComponent implements OnInit, OnDestroy {
  @Select(SearchFilterQueries.dealerFilter) readonly dealerFilter$: Observable<DealerFilter>;
  @ViewChild('selectAllDealers') selectedAllDealersCheckBox: ElementRef<HTMLInputElement>;
  @ViewChildren('dealerWithCheckbox') dealersWithCheckBox: QueryList<ElementRef<HTMLInputElement>>;
  @Input() dealersState: Observable<FilterState>;
  @Output() toggleAll = new EventEmitter<void | string[]>();
  @Output() selectDealer = new EventEmitter<string>();
  readonly showMoreLess = new Subject();
  dealersShown$: Observable<number>;
  showMore$: Observable<boolean>;
  readonly initialDealersShown = 9;

  searchDealer: string;
  private destroyed$ = new Subject();
  private searchTerm = new BehaviorSubject('');

  dealerList$: Observable<SelectableDealer[]>;

  constructor(readonly store: Store) {}

  ngOnInit() {
    this.dealerList$ = combineLatest(this.searchTerm.asObservable(), this.dealersState).pipe(
      takeUntil(this.destroyed$),
      map(([searchTerm, dealerState]) => {
        if (searchTerm === '') {
          return dealerState.dealerList;
        }
        return dealerState.dealerList.filter(d => d.name.toLocaleLowerCase().includes(searchTerm));
      })
    );
    const dealerCount$ = this.dealerList$.pipe(map(dealers => dealers.length));
    // The current step is rest every time the dealer count updates - whether on inline search or a new request
    const currentStep$ = dealerCount$.pipe(
      distinctUntilChanged(),
      switchMap(count => {
        const stepForDealerCount = stepForCountReducer(this.initialDealersShown, 10);
        return this.showMoreLess
          .asObservable()
          .pipe(mapTo(count), scan(stepForDealerCount, 0), startWith(0), shareReplay(1));
      })
    );
    this.dealersShown$ = currentStep$.pipe(map(dealersShown(this.initialDealersShown, 10)), tap(console.log));

    this.showMore$ = combineLatest([currentStep$, dealerCount$]).pipe(
      map(([step, dealerCount]) => {
        return maxStepsForCount(this.initialDealersShown, 10)(dealerCount) === step ? false : true;
      })
    );
  }

  search(e) {
    this.searchDealer = e.target.value.replace(/[^ -~]+/g, '').toLowerCase();
    this.searchTerm.next(this.searchDealer);
  }

  dealerById(index: number, dealer: SelectableDealer) {
    return dealer.organizationEntityUuid;
  }

  toggleAllDealers() {
    this.selectedAllDealersCheckBox.nativeElement.checked = !this.selectedAllDealersCheckBox.nativeElement.checked;
    this.dealerList$.pipe(take(1)).subscribe(value => {
      if (this.searchDealer !== undefined && this.searchDealer.length > 0) {
        this.selectedAllDealersCheckBox.nativeElement.checked = true;
        this.toggleAll.emit(value.map(d => d.organizationEntityUuid));
      } else {
        this.toggleAll.emit();
      }
    });
  }

  checkDealer(id, event) {
    if (this.searchDealer !== undefined && this.searchDealer.length > 0) {
      const allBoxesChecked = this.dealersWithCheckBox.map(d => d.nativeElement.checked).every(s => s);
      if (this.selectedAllDealersCheckBox !== undefined) {
        this.selectedAllDealersCheckBox.nativeElement.checked = allBoxesChecked && event.target.checked;
      }
    }
    this.selectDealer.emit(id);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
